// ─────────────────────────────────────────────────────────────────────────────
// V2Purchases — /dashboard/purchase-entry (purchase bills) in the v2 design.
// Endpoints are v1's: GET/POST /api/purchases, PUT /:id, PATCH /:id/payment,
// GET /api/purchase-orders/unconverted, GET /api/purchase-orders/:id.
// Arriving with location.state.importPoId opens the editor prefilled from that
// PO (same as v1). Body shapes identical to v1 (incl. fromPurchaseOrderId).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import usePageTitle from "../hooks/usePageTitle";
import userContext from "../context/user/userContext";
import API_BASE from "../apiConfig";
import { Glyph, Duo } from "./icons";
import { MenuButton, Money, Segmented, Sheet, Skel, Switch, inr } from "./primitives";
import { Combo, Field, LineItems, PageHead, Stat, fromPurchaseItems, newLine, purchasePrice, purchaseTotals, toPurchaseItems, useProducts } from "./kit";
import { TotalsBox } from "./V2PurchaseOrders";

const BASE = `${API_BASE}/api`;
const json = () => ({ "Content-Type": "application/json", "auth-token": localStorage.getItem("token") });
const today = () => new Date().toISOString().split("T")[0];
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
const CATS = ["Purchases", "Raw Material", "Packing Material", "Consumables", "Tools & Equipment", "Other"];
const MODES = ["Cash", "Cheque", "UPI", "Bank Transfer", "NEFT", "RTGS"];
const overdue = (p) => p.status !== "PAID" && p.dueDate && new Date(p.dueDate) < new Date();

function PayStatus({ p }) {
  if (p.status === "PAID") return <span className="v2-status v2-status--good"><Glyph name="check" size={13} stroke={2.2} /> Paid</span>;
  if (overdue(p)) return <span className="v2-status v2-status--bad"><Glyph name="alert" size={13} stroke={2.2} /> Overdue</span>;
  if (p.status === "PARTIAL") return <span className="v2-status v2-status--warn"><Glyph name="clock" size={13} stroke={2.2} /> {inr(p.balanceDue)} due</span>;
  return <span className="v2-status v2-status--warn"><Glyph name="clock" size={13} stroke={2.2} /> Unpaid</span>;
}

export default function V2Purchases({ showAlert }) {
  usePageTitle("Purchase Entry");
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = useContext(userContext);
  const canWrite = hasPermission("purchases", "write");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("ALL");
  const [q, setQ] = useState("");
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);   // null | { importPoId? } | purchase
  const [paying, setPaying] = useState(null);
  const newBtn = useRef(null);

  const load = async () => {
    setLoading(true);
    try { const r = await fetch(`${BASE}/purchases`, { headers: json() }); const d = await r.json(); setList(Array.isArray(d) ? d : []); }
    catch { setList([]); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  // "Convert to bill" from a PO
  useEffect(() => {
    if (location.state?.importPoId) { setEditing({ importPoId: location.state.importPoId }); navigate(location.pathname, { replace: true, state: null }); }
  }, []); // eslint-disable-line

  const shown = list.filter((p) => (status === "ALL" || (status === "OVERDUE" ? overdue(p) : p.status === status)) &&
    (!q.trim() || `${p.purchaseNumber} ${p.supplierInvoiceNo || ""} ${p.supplierId?.supplierName || ""}`.toLowerCase().includes(q.trim().toLowerCase())))
    .sort((a, b) => new Date(b.purchaseDate) - new Date(a.purchaseDate));
  const unpaidTotal = list.filter((p) => p.status !== "PAID").reduce((a, p) => a + (p.balanceDue || 0), 0);
  const thisMonth = list.filter((p) => { const d = new Date(p.purchaseDate); const n = new Date(); return d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear(); });

  const menu = (p) => [
    { label: "View bill", glyph: "invoice", onSelect: () => setViewing(p) },
    ...(canWrite && p.status !== "PAID" ? [{ label: "Record payment", glyph: "cash", onSelect: () => setPaying(p) }] : []),
    ...(canWrite ? [{ label: "Edit", glyph: "note", onSelect: () => setEditing(p) }] : []),
  ];

  return (
    <div className="v2p">
      <PageHead eyebrow="Payables" title="Purchase bills" sub="Supplier invoices you've received — stock and supplier ledger update automatically."
        actions={canWrite && <button ref={newBtn} type="button" className="v2-btn v2-btn--primary" onClick={() => setEditing({})}><Glyph name="plus" size={17} stroke={2.2} /> New bill</button>} />
      <div className="v2k-stats">
        <Stat label="To pay" value={loading ? "—" : inr(unpaidTotal)} note={`${list.filter((p) => p.status !== "PAID").length} open bills`} />
        <Stat label="Overdue" value={loading ? "—" : list.filter(overdue).length} note={inr(list.filter(overdue).reduce((a, p) => a + (p.balanceDue || 0), 0))} tone={list.some(overdue) ? "warn" : undefined} />
        <Stat label="Bought this month" value={loading ? "—" : inr(thisMonth.reduce((a, p) => a + (p.grandTotal || 0), 0))} note={`${thisMonth.length} bills`} />
        <Stat label="Input GST this month" value={loading ? "—" : inr(thisMonth.reduce((a, p) => a + (p.totalCgst || 0) + (p.totalSgst || 0) + (p.totalIgst || 0), 0))} note="Claimable as ITC" />
      </div>
      <div className="v2i-toolbar">
        <Segmented ariaLabel="Status" value={status} onChange={setStatus} options={[{ value: "ALL", label: "All" }, { value: "UNPAID", label: "Unpaid" }, { value: "PARTIAL", label: "Partial" }, { value: "OVERDUE", label: "Overdue" }, { value: "PAID", label: "Paid" }]} />
        <div className="v2-field--search v2i-search"><Glyph name="search" size={17} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Bill no., supplier invoice, supplier" aria-label="Search bills" /></div>
      </div>
      <section className="v2-card v2i-card">
        {loading ? <div className="v2-list" style={{ padding: 8 }}>{[0, 1, 2, 3].map((i) => <Skel key={i} h={58} r={16} />)}</div>
          : shown.length === 0 ? <div className="v2c-empty"><Duo name="invoice" size={48} /><strong>{list.length ? "Nothing in this view" : "No purchase bills yet"}</strong><span>Record what suppliers bill you — stock goes up and you'll see what you owe.</span></div>
          : <div className="v2i-rows">{shown.map((p) => (
            <div key={p._id} className="v2cn-row">
              <button type="button" className="v2cn-open" onClick={() => setViewing(p)}>
                <Duo name="invoice" size={30} />
                <span className="v2-row-main">
                  <span className="v2i-no">{p.purchaseNumber}{p.supplierInvoiceNo && <span className="v2cn-link">{p.supplierInvoiceNo}</span>}</span>
                  <span className="v2-row-sub">{p.supplierId?.supplierName || "Supplier"} · {fmtDate(p.purchaseDate)}{p.dueDate && p.status !== "PAID" ? ` · due ${fmtDate(p.dueDate)}` : ""}</span>
                </span>
                <span className="v2cn-end"><span className="v2i-total">{inr(p.grandTotal, { decimals: 2 })}</span><PayStatus p={p} /></span>
              </button>
              <MenuButton items={menu(p)} className="v2-icon-btn v2i-more" label={`Actions for ${p.purchaseNumber}`} width={220} />
            </div>))}</div>}
      </section>

      <BillDetail p={viewing} canWrite={canWrite} onClose={() => setViewing(null)} onPay={(p) => { setViewing(null); setPaying(p); }} onEdit={(p) => { setViewing(null); setEditing(p); }} />
      <PurchaseEditor value={editing} originRef={newBtn} onClose={() => setEditing(null)}
        onSaved={(d, msg) => { setEditing(null); showAlert?.(msg, "success"); load(); }} />
      <PaySheet p={paying} onClose={() => setPaying(null)} onDone={(msg) => { setPaying(null); showAlert?.(msg, "success"); load(); }} />
    </div>
  );
}

function BillDetail({ p: live, canWrite, onClose, onPay, onEdit }) {
  const keep = useRef(live); if (live) keep.current = live;
  const p = keep.current;
  if (!p) return null;
  return (
    <Sheet open={!!live} onClose={onClose} width={640} title={p.purchaseNumber} subtitle={`${p.supplierId?.supplierName || "Supplier"}${p.supplierInvoiceNo ? ` · invoice ${p.supplierInvoiceNo}` : ""}`}
      footer={<>{canWrite && <button type="button" className="v2-btn" onClick={() => onEdit(p)}>Edit</button>}
        {canWrite && p.status !== "PAID" && <button type="button" className="v2-btn v2-btn--primary" onClick={() => onPay(p)}><Glyph name="cash" size={15} /> Record payment</button>}</>}>
      <div className="v2pub-receipt">
        <div className="v2pub-receipt-top">
          <div><span className="v2-label">{p.status === "PAID" ? "Paid in full" : "Balance due"}</span><div className="v2pub-receipt-amt"><Money value={p.status === "PAID" ? p.grandTotal : p.balanceDue} /></div></div>
          <PayStatus p={p} />
        </div>
        <dl className="v2pub-meta">
          <div><dt>Billed</dt><dd>{fmtDate(p.purchaseDate)}</dd></div>
          <div><dt>Due</dt><dd>{fmtDate(p.dueDate)}</dd></div>
          <div><dt>Total</dt><dd>{inr(p.grandTotal, { decimals: 2 })}</dd></div>
          <div><dt>Category</dt><dd>{p.expenseCategory || "Purchases"}</dd></div>
        </dl>
        <div className="v2-inset">{(p.items || []).map((it, i) => (
          <div key={i} className="v2-inset-row v2-inset-row--static">
            <span className="v2-row-main"><span className="v2-row-title v2-wrap">{it.name}</span><span className="v2-row-sub">{it.qty} {it.unit || ""} × {inr(it.price, { decimals: 2 })} · GST {it.gstRate}%</span></span>
            <b>{inr((it.qty || 0) * (it.price || 0), { decimals: 2 })}</b>
          </div>))}</div>
        {(p.payments || []).length > 0 && (<>
          <div className="v2c-section-title" style={{ margin: "4px 0 0 4px" }}>Payments</div>
          <div className="v2-inset">{p.payments.map((pm, i) => (
            <div key={i} className="v2-inset-row v2-inset-row--static">
              <span className="v2-row-main"><span className="v2-row-title">{inr(pm.amount, { decimals: 2 })}</span><span className="v2-row-sub">{fmtDate(pm.date || pm.paymentDate)} · {pm.paymentMode || "—"}{pm.reference ? ` · ${pm.reference}` : ""}</span></span>
            </div>))}</div>
        </>)}
        {p.fromPurchaseOrderId && <p className="v2-row-sub">Created from a purchase order.</p>}
      </div>
    </Sheet>
  );
}

function PurchaseEditor({ value, originRef, onClose, onSaved }) {
  const edit = value && value._id ? value : null;
  const { products, loading } = useProducts();
  const [suppliers, setSuppliers] = useState([]);
  const [f, setF] = useState({});
  const [lines, setLines] = useState(() => [newLine()]);
  const [fromPo, setFromPo] = useState(null);
  const [pos, setPos] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const was = useRef(null);
  useEffect(() => { fetch(`${BASE}/suppliers`, { headers: json() }).then((r) => r.json()).then((d) => setSuppliers(Array.isArray(d) ? d : [])).catch(() => {}); }, []);

  const applyPO = (po) => {
    setF((p) => ({ ...p, supplierId: po.supplierId?._id || po.supplierId || "", supplyType: po.supplyType || "intra" }));
    setLines(po.items?.length ? fromPurchaseItems(po.items) : [newLine()]);
    setFromPo({ id: po._id, label: `${po.poNumber}${po.supplierId?.supplierName ? ` · ${po.supplierId.supplierName}` : ""}` });
  };
  useEffect(() => {
    if (!value || was.current === value) return;
    was.current = value; setErr(""); setFromPo(null); setPos(null);
    setF({
      supplierId: edit?.supplierId?._id || edit?.supplierId || "", supplierInvoiceNo: edit?.supplierInvoiceNo || "",
      purchaseDate: edit?.purchaseDate?.split("T")[0] || today(), dueDate: edit?.dueDate?.split("T")[0] || "",
      supplyType: edit?.supplyType || "intra", expenseCategory: edit?.expenseCategory || "Purchases", notes: edit?.notes || "", updateStock: !edit,
    });
    setLines(edit?.items?.length ? fromPurchaseItems(edit.items) : [newLine()]);
    if (value.importPoId) fetch(`${BASE}/purchase-orders/${value.importPoId}`, { headers: json() }).then((r) => r.json()).then((po) => { if (po && !po.error) applyPO(po); }).catch(() => {});
  }, [value]); // eslint-disable-line
  useEffect(() => { if (!value) was.current = null; }, [value]);

  const loadPOs = async () => {
    setPos("loading");
    try { const r = await fetch(`${BASE}/purchase-orders/unconverted`, { headers: json() }); const d = await r.json(); setPos(Array.isArray(d) ? d : []); }
    catch { setPos([]); }
  };
  const t = useMemo(() => purchaseTotals(lines, f.supplyType), [lines, f.supplyType]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const save = async (e) => {
    e.preventDefault(); setErr("");
    if (!f.supplierId) { setErr("Choose a supplier."); return; }
    if (!f.supplierInvoiceNo?.trim()) { setErr("The supplier's invoice number is required."); return; }
    const items = toPurchaseItems(lines);
    if (!items.some((i) => i.name && i.qty > 0)) { setErr("Add at least one item."); return; }
    setBusy(true);
    try {
      const body = { ...f, items, ...(fromPo && !edit ? { fromPurchaseOrderId: fromPo.id } : {}) };
      const r = await fetch(edit ? `${BASE}/purchases/${edit._id}` : `${BASE}/purchases`, { method: edit ? "PUT" : "POST", headers: json(), body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't save");
      onSaved(d, edit ? "Bill updated" : `Bill ${d.purchaseNumber || ""} recorded`);
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  const supplierItems = suppliers.map((s) => ({ id: s._id, label: s.supplierName, sub: s.supplierGSTIN || "" }));

  return (
    <Sheet open={!!value} onClose={onClose} originRef={edit ? undefined : originRef} width={800} title={edit ? `Edit ${edit.purchaseNumber}` : "New purchase bill"}
      subtitle={fromPo ? `From ${fromPo.label}` : "Record a supplier's invoice"}
      footer={<><span className="v2cn-foot-total">Bill <b>{inr(t.total, { decimals: 2 })}</b></span>
        <button type="button" className="v2-btn" onClick={onClose}>Cancel</button>
        <button type="submit" form="v2pe-form" className="v2-btn v2-btn--primary" disabled={busy}>{busy ? "Saving…" : edit ? "Save changes" : "Record bill"}</button></>}>
      <form id="v2pe-form" className="v2-form" onSubmit={save}>
        {!edit && !fromPo && (
          <div className="v2pe-import">
            {pos === null ? <button type="button" className="v2-btn v2-btn--sm" onClick={loadPOs}><Glyph name="truck" size={15} /> Import from a purchase order</button>
              : pos === "loading" ? <span className="v2-row-sub"><span className="v2-spin" /> Loading open orders…</span>
              : pos.length === 0 ? <span className="v2-row-sub">No open purchase orders to import.</span>
              : <div className="v2i-presets">{pos.map((po) => <button key={po._id} type="button" className="v2-chip-btn v2i-preset" onClick={() => applyPO(po)}>{po.poNumber} · {po.supplierId?.supplierName || ""}</button>)}</div>}
          </div>
        )}
        <div className="v2-form-row">
          <Field label="Supplier"><Combo items={supplierItems} value={f.supplierId} onChange={(id) => setF((p) => ({ ...p, supplierId: id }))} placeholder="Choose a supplier" searchPlaceholder="Search suppliers" ariaLabel="Supplier" /></Field>
          <Field label="Supplier's invoice no."><input className="v2-input v2-mono" value={f.supplierInvoiceNo || ""} onChange={set("supplierInvoiceNo")} /></Field>
        </div>
        <div className="v2-form-row">
          <Field label="Bill date"><input className="v2-input" type="date" value={f.purchaseDate || ""} onChange={set("purchaseDate")} /></Field>
          <Field label="Due date" optional><input className="v2-input" type="date" min={f.purchaseDate} value={f.dueDate || ""} onChange={set("dueDate")} /></Field>
        </div>
        <div className="v2-form-row">
          <Field label="Supply"><Segmented ariaLabel="Supply" value={f.supplyType || "intra"} onChange={(v) => setF((p) => ({ ...p, supplyType: v }))} options={[{ value: "intra", label: "Intra-state" }, { value: "inter", label: "Inter-state" }]} /></Field>
          <Field label="Category"><select className="v2-input" value={f.expenseCategory || "Purchases"} onChange={set("expenseCategory")}>{CATS.map((c) => <option key={c}>{c}</option>)}</select></Field>
        </div>
        <div className="v2c-section-title" style={{ margin: "6px 0 0 4px" }}>Items</div>
        <LineItems items={lines} onChange={setLines} products={products} productsLoading={loading} defaultGst={18} priceFor={purchasePrice} compact />
        <TotalsBox t={t} supplyType={f.supplyType} label="Bill total" />
        {!edit && <div className="v2-inset"><Switch id="v2pe-stock" checked={!!f.updateStock} onChange={(v) => setF((p) => ({ ...p, updateStock: v }))} label="Add items to stock" description="Increases stock for lines linked to inventory products" /></div>}
        <Field label="Notes" optional><input className="v2-input" value={f.notes || ""} onChange={set("notes")} /></Field>
        {err && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {err}</div>}
      </form>
    </Sheet>
  );
}

function PaySheet({ p: live, onClose, onDone }) {
  const keep = useRef(live); if (live) keep.current = live;
  const p = keep.current;
  const [amount, setAmount] = useState(""); const [date, setDate] = useState(today()); const [mode, setMode] = useState("Cash");
  const [reference, setReference] = useState(""); const [note, setNote] = useState(""); const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  useEffect(() => { if (live) { setAmount(""); setDate(today()); setMode("Cash"); setReference(""); setNote(""); setErr(""); } }, [live]);
  if (!p) return null;
  const amt = parseFloat(amount) || 0;
  const go = async (e) => {
    e.preventDefault(); setErr("");
    if (!(amt > 0)) { setErr("Enter an amount greater than 0."); return; }
    if (amt > (p.balanceDue || 0) + 0.01) { setErr(`That's more than the balance due (${inr(p.balanceDue, { decimals: 2 })}).`); return; }
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/purchases/${p._id}/payment`, { method: "PATCH", headers: json(), body: JSON.stringify({ amount: amt, paymentDate: date, paymentMode: mode, reference, note }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't record the payment");
      onDone(`Payment of ${inr(amt, { decimals: 2 })} recorded`);
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  return (
    <Sheet open={!!live} onClose={onClose} width={480} title="Pay supplier" subtitle={`${p.purchaseNumber} · ${p.supplierId?.supplierName || ""}`}
      footer={<><button type="button" className="v2-btn" onClick={onClose}>Cancel</button><button type="submit" form="v2pe-pay" className="v2-btn v2-btn--primary" disabled={busy}>{busy ? "Saving…" : amt > 0 ? `Pay ${inr(amt, { decimals: 2 })}` : "Record payment"}</button></>}>
      <form id="v2pe-pay" className="v2-form" onSubmit={go}>
        <div className="v2-inset v2-summary">
          <div className="v2-inset-row v2-inset-row--static"><span>Bill total</span><b>{inr(p.grandTotal, { decimals: 2 })}</b></div>
          <div className="v2-inset-row v2-inset-row--static"><span>Paid so far</span><b>{inr(p.amountPaid, { decimals: 2 })}</b></div>
          <div className="v2-inset-row v2-inset-row--static"><span>Balance due</span><b className="v2-accent-text">{inr(p.balanceDue, { decimals: 2 })}</b></div>
        </div>
        <Field label="Amount paid">
          <span className="v2-input-wrap v2-input-wrap--money"><span className="v2-input-prefix">₹</span>
            <input className="v2-input v2-input--lg" type="number" step="0.01" min="0" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" autoFocus />
            <button type="button" className="v2-chip-btn" onClick={() => setAmount(String(p.balanceDue))}>Full due</button></span>
        </Field>
        {amt > 0 && amt < (p.balanceDue || 0) && <div className="v2-banner v2-banner--warn"><Glyph name="clock" size={16} /> {inr((p.balanceDue || 0) - amt, { decimals: 2 })} will still be due.</div>}
        <div className="v2-form-row">
          <Field label="Date"><input className="v2-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          <Field label="Mode"><select className="v2-input" value={mode} onChange={(e) => setMode(e.target.value)}>{MODES.map((m) => <option key={m}>{m}</option>)}</select></Field>
        </div>
        <div className="v2-form-row">
          <Field label="Reference" optional><input className="v2-input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="UTR / cheque no." /></Field>
          <Field label="Note" optional><input className="v2-input" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        </div>
        {err && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {err}</div>}
      </form>
    </Sheet>
  );
}
